/**
 * /academy/course/lesson/:id — one lesson of the path (IELTS or TCF, D-107): objectives, the
 * explanation in the exam language (the IELTS reveals the French help, the TCF the English help),
 * vocabulary cards read aloud (en-GB or fr-FR), « À faire / À éviter » of a method lesson, the help
 * translated into the learner's mother tongue and read aloud, then the exercises one by one with
 * an immediate correction, and a closing screen (score, XP, next lesson). A lesson reopened
 * resumes at the first exercise not yet done.
 */

import {
  ApiError,
  academyCourseExercisesRecordingCreate,
  academyCourseLessonsAnswerCreate,
  academyCourseLessonsRestartCreate,
  getAcademyCourseRetrieveQueryKey,
  getAcademyCourseLessonsRetrieveQueryKey,
  useAcademyCourseLessonsRetrieve,
  useAcademyMeRetrieve,
} from "@agencyos/api-client";
import {
  type DoAvoid,
  type Feedback,
  type Lesson,
  type LessonProgressView,
  type LessonTranslation,
  type VocabularyCard,
  helpLanguage,
  explanationLanguage,
  type ContentLanguage,
  parseDoAvoid,
  parseFeedback,
  parseLesson,
  parseProgressView,
  pick,
} from "@agencyos/core";
import { Badge, Button, EmptyState, GlassCard, Skeleton, buttonClass } from "@agencyos/ui-web";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, BookOpen, CircleCheck, CircleX, Languages, ListChecks, Lock, PartyPopper, RotateCcw, Sparkles, Target, Volume2 } from "lucide-react";
import { type CSSProperties, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useNavigate, useParams } from "react-router-dom";

import { extensionFor } from "../audio";
import { Ring } from "../ui";
import { ContentLangProvider, useSpeakContent } from "./contentLang";
import { useCourseText } from "./courseText";
import { courseExam, useLessonExamParams } from "./exam";
import { ExerciseCard } from "./Exercise";
import { LessonHelp } from "./LessonHelp";
import { Markdown } from "./Markdown";

type Step = "learn" | "practice" | "end";

function VocabCard({ card, french, index, lang, meaning }: { card: VocabularyCard; french: boolean; index: number; lang: ContentLanguage; meaning: string }) {
  const { t, i18n } = useTranslation();
  const speak = useSpeakContent();
  // German (D-118): the meaning in the learner's language, always shown
  const help = lang === "de" ? (i18n.language.startsWith("en") ? card.en || card.fr : card.fr || card.en) : lang === "en" ? card.fr : card.en;
  return (
    <li className="ac-vocab ao-enter" style={{ "--ao-i": index } as CSSProperties}>
      <div className="ac-vocab__head">
        <strong className="ac-vocab__word" lang={lang}>
          {card.word}
        </strong>
        <button type="button" className="ac-icon-button ac-icon-button--small" onClick={() => void speak(card.example ? `${card.word}. ${card.example}` : card.word)}>
          <Volume2 aria-hidden size={16} />
          <span className="visually-hidden">{t("ielts.lesson.pronounce", { word: card.word })}</span>
        </button>
      </div>
      {card.definition && (
        <p className="ac-vocab__def" lang={lang}>
          {card.definition}
        </p>
      )}
      {card.example && (
        <p className="ac-vocab__example" lang={lang}>
          “{card.example}”
        </p>
      )}
      {(french || lang === "de") && help && (
        <p className="ac-vocab__fr" lang={lang === "en" ? "fr" : lang === "de" ? i18n.language : "en"}>
          {help}
        </p>
      )}
      {meaning && (
        <p className="ac-vocab__fr ac-vocab__translated">
          <span className="visually-hidden">{t("academyCourse.translate.badge")} </span>
          {meaning}
        </p>
      )}
    </li>
  );
}

/** « À faire / À éviter » of a method lesson, as two cards. */
function DoAvoidCards({ value, translated }: { value: DoAvoid; translated: LessonTranslation | null }) {
  const { t } = useTranslation();
  const doList = translated?.doList.length ? translated.doList : value.doList;
  const avoidList = translated?.avoidList.length ? translated.avoidList : value.avoidList;
  if (!doList.length && !avoidList.length) return null;
  return (
    <div className="ac-grid ac-do-avoid">
      {doList.length > 0 && (
        <GlassCard as="section" className="ac-card ac-span-6 ac-do" aria-labelledby="ac-do">
          <h2 id="ac-do" className="ac-card-title">
            <span className="ac-pastille ac-tone--success" aria-hidden="true">
              <CircleCheck size={16} />
            </span>
            <span>{t("academyCourse.method.do")}</span>
          </h2>
          <ul className="ac-checklist">
            {doList.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </GlassCard>
      )}
      {avoidList.length > 0 && (
        <GlassCard as="section" className="ac-card ac-span-6 ac-avoid" aria-labelledby="ac-avoid">
          <h2 id="ac-avoid" className="ac-card-title">
            <span className="ac-pastille ac-tone--danger" aria-hidden="true">
              <CircleX size={16} />
            </span>
            <span>{t("academyCourse.method.avoid")}</span>
          </h2>
          <ul className="ac-avoid-list">
            {avoidList.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </GlassCard>
      )}
    </div>
  );
}

function Steps({ step }: { step: Step }) {
  const { t } = useTranslation();
  const steps: Step[] = ["learn", "practice", "end"];
  const index = steps.indexOf(step);
  return (
    <ol className="ac-steps" aria-label={t("ielts.lesson.steps")}>
      {steps.map((s, i) => (
        <li key={s} className={`ac-steps__item${i < index ? " ac-steps__item--done" : ""}${i === index ? " ac-steps__item--on" : ""}`} aria-current={i === index ? "step" : undefined}>
          <span className="ac-steps__n ao-num" aria-hidden="true">
            {i + 1}
          </span>
          {t(`ielts.lesson.step.${s}`)}
        </li>
      ))}
    </ol>
  );
}

interface LearnProps {
  lesson: Lesson;
  exam: string;
  doAvoid: DoAvoid;
  motherTongue: string;
  translation: LessonTranslation | null;
  onTranslation: (t: LessonTranslation | null) => void;
  onStart: () => void;
}

function LearnStep({ lesson, exam, doAvoid, motherTongue, translation, onTranslation, onStart }: LearnProps) {
  const { t } = useTranslation();
  const { ct, contentLang } = useCourseText(exam);
  const { i18n } = useTranslation();
  const [french, setFrench] = useState(false);
  const helpLang = helpLanguage(exam, i18n.language);
  // German (D-118): the help is in French or English, the other of the interface language
  const helpText = (key: string) => (exam === "goethe" ? t(helpLang === "fr" ? `ielts.lesson.${key}` : `academyCourse.tcf.lesson.${key}`) : ct(`lesson.${key}`));
  const help = lesson.explanation[helpLang] ?? "";
  const main = lesson.explanation[explanationLanguage(exam, i18n.language)] ?? "";
  return (
    <div className="ac-lesson__learn">
      <div className="ac-grid">
        <GlassCard as="section" className="ac-card ac-span-8" aria-labelledby="ac-lesson-explain">
          <div className="ac-card__head">
            <h2 id="ac-lesson-explain" className="ac-card-title">
              <span className="ac-pastille ac-tone--accent" aria-hidden="true">
                <BookOpen size={16} />
              </span>
              <span>{t("ielts.lesson.explanation")}</span>
            </h2>
            {help && (
              <Button variant="tinted" size="sm" icon={<Languages aria-hidden size={15} />} aria-expanded={french} onClick={() => setFrench(!french)}>
                {french ? helpText("hideFrench") : helpText("showFrench")}
              </Button>
            )}
          </div>
          {main ? <Markdown source={main} lang={explanationLanguage(exam, i18n.language)} /> : <p className="ac-secondary">{ct("lesson.noExplanation")}</p>}
          {french && help && (
            <div className="ac-help-fr ac-help-fr--block" lang={helpLang}>
              <p className="ac-label">{helpText("frenchHelp")}</p>
              <Markdown source={help} lang={helpLang} />
            </div>
          )}
        </GlassCard>
        <GlassCard as="section" className="ac-card ac-span-4 ac-lesson__goals" aria-labelledby="ac-lesson-goals">
          <h2 id="ac-lesson-goals" className="ac-card-title">
            <span className="ac-pastille ac-tone--success" aria-hidden="true">
              <Target size={16} />
            </span>
            <span>{t("ielts.lesson.objectives")}</span>
          </h2>
          <ul className="ac-checklist" lang={contentLang}>
            {lesson.objectives.map((objective) => (
              <li key={objective}>{objective}</li>
            ))}
          </ul>
          <p className="ac-footnote">
            <ListChecks aria-hidden size={14} /> {t("ielts.lesson.exerciseCount", { count: lesson.exercises.length })}
          </p>
        </GlassCard>
      </div>
      <DoAvoidCards value={doAvoid} translated={translation} />
      <LessonHelp lessonId={lesson.id} motherTongue={motherTongue} onTranslation={onTranslation} />
      {lesson.vocabulary.length > 0 && (
        <section className="ac-section" aria-labelledby="ac-lesson-vocab">
          <div className="ac-section-title">
            <h2 id="ac-lesson-vocab">{t("ielts.lesson.vocabulary")}</h2>
            <span className="ac-caption">{ct("lesson.vocabHint")}</span>
          </div>
          <ul className="ac-vocab-grid">
            {lesson.vocabulary.map((card, index) => (
              <VocabCard key={card.word} card={card} french={french} index={index} lang={contentLang} meaning={translation?.vocabulary[card.word] ?? ""} />
            ))}
          </ul>
        </section>
      )}
      <div className="ac-question__actions">
        <Button size="lg" onClick={onStart} disabled={!lesson.exercises.length}>
          {t("ielts.lesson.practise")}
          <ArrowRight aria-hidden size={18} />
        </Button>
      </div>
    </div>
  );
}

/** A new lesson (the « next lesson » button) starts from a fresh screen. */
export function LessonPage() {
  const { id = "" } = useParams();
  return <LessonView key={id} id={id} />;
}

function LessonView({ id }: { id: string }) {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const lessonParams = useLessonExamParams(); // D-109: a TCF lesson opened from the TEF path
  const query = useAcademyCourseLessonsRetrieve(id, lessonParams, { query: { retry: false, staleTime: 0 } });
  const raw = query.data?.status === 200 ? query.data.data : null;
  const lesson = useMemo(() => (raw ? parseLesson(raw) : null), [raw]);
  const exam = raw?.exam ?? "ielts_general";
  const isMethod = raw?.method === true;
  const doAvoid = useMemo(() => parseDoAvoid(raw?.do_avoid, i18n.language), [raw, i18n.language]);
  const { ct, contentLang } = useCourseText(exam);
  const me = useAcademyMeRetrieve({ query: { retry: false, staleTime: 60_000 } });
  const motherTongue = me.data?.status === 200 ? (me.data.data.mother_tongue ?? "") : "";
  const [translation, setTranslation] = useState<LessonTranslation | null>(null);
  const keepTranslation = useCallback((value: LessonTranslation | null) => setTranslation(value), []);
  const [step, setStep] = useState<Step>("learn");
  const [index, setIndex] = useState(0);
  const [results, setResults] = useState<Record<string, Feedback>>({});
  const [progress, setProgress] = useState<LessonProgressView | null>(null);
  const [nextLesson, setNextLesson] = useState<string | null>(null);
  const [xp, setXp] = useState(0);
  const [restarting, setRestarting] = useState(false);
  const started = useRef("");
  const heading = useRef<HTMLHeadingElement>(null);

  // A lesson reopened: resume at the first exercise not done yet.
  useEffect(() => {
    if (!lesson || started.current === lesson.id) return;
    started.current = lesson.id;
    const first = lesson.exercises.findIndex((e) => !e.done);
    setIndex(first < 0 ? 0 : first);
    setProgress(lesson.progress);
    if (first < 0 && lesson.exercises.length) setStep("end");
  }, [lesson]);

  // A new step or exercise: the title takes the focus (screen readers), not on the first display.
  const moved = useRef(false);
  useEffect(() => {
    if (moved.current) heading.current?.focus();
    moved.current = true;
  }, [step, index]);

  if (query.isLoading) {
    return (
      <div className="ac-page">
        <Skeleton style={{ height: 72, maxWidth: 480 }} />
        <Skeleton style={{ height: 360 }} />
      </div>
    );
  }
  if (query.error instanceof ApiError && (query.error.status === 403 || query.error.status === 400)) {
    return (
      <div className="ac-page">
        <Link to="/academy/course" className="ac-back">
          <ArrowLeft aria-hidden size={16} />
          {t("ielts.lesson.back")}
        </Link>
        <GlassCard className="ac-card">
          <EmptyState icon={<Lock aria-hidden size={22} />} title={t("ielts.lesson.lockedTitle")} description={query.error.detail ?? t("ielts.lesson.lockedText")} />
        </GlassCard>
      </div>
    );
  }
  if (!lesson) {
    return (
      <GlassCard className="ac-card ac-card--center">
        <p className="ko">{t("common.error")}</p>
        <Button variant="secondary" onClick={() => void query.refetch()}>
          {t("common.retry")}
        </Button>
      </GlassCard>
    );
  }

  const total = lesson.exercises.length;
  const exercise = lesson.exercises[index];
  const title = pick(lesson.title, contentLang);
  const backTo = isMethod ? "/academy/course/method" : "/academy/course";
  const record = (exerciseId: string, value: unknown) => {
    const feedback = parseFeedback(value);
    setResults((prev) => ({ ...prev, [exerciseId]: feedback }));
    setXp((v) => v + feedback.xpGained);
    if (feedback.nextLesson) setNextLesson(feedback.nextLesson);
    const data = value && typeof value === "object" && "progress" in value ? (value as { progress: unknown }).progress : null;
    if (data) setProgress(parseProgressView(data));
    return feedback;
  };
  const check = async (exerciseId: string, response: unknown) => {
    const answer = await academyCourseLessonsAnswerCreate(id, { exercise: exerciseId, response }, lessonParams);
    return record(exerciseId, answer.data);
  };
  const sendRecording = async (exerciseId: string, audio: Blob) => {
    const type = audio.type || "audio/webm";
    const file = new File([audio], `speak.${extensionFor(type)}`, { type });
    const answer = await academyCourseExercisesRecordingCreate(exerciseId, { audio: file }, lessonParams);
    return record(exerciseId, answer.data);
  };
  const goNext = () => {
    if (index + 1 < total) setIndex(index + 1);
    else {
      setStep("end");
      void queryClient.invalidateQueries({ queryKey: getAcademyCourseRetrieveQueryKey({ exam: courseExam(exam) }) });
    }
  };
  const restart = async () => {
    setRestarting(true);
    try {
      await academyCourseLessonsRestartCreate(id, lessonParams);
      started.current = "";
      setResults({});
      setXp(0);
      await queryClient.invalidateQueries({ queryKey: getAcademyCourseLessonsRetrieveQueryKey(id) });
      setStep("practice");
      setIndex(0);
    } finally {
      setRestarting(false);
    }
  };

  const answeredNow = exercise ? (results[exercise.id] ?? exercise.done?.feedback ?? null) : null;
  const allResults = lesson.exercises.filter((e) => e.auto).map((e) => results[e.id] ?? e.done?.feedback ?? null);
  const right = allResults.filter((f) => f?.correct === true).length;
  const autoCount = lesson.exercises.filter((e) => e.auto).length;
  const score = progress?.score ?? (autoCount ? right / autoCount : 1);
  const passed = progress?.status === "completed";

  const subtitle = pick(lesson.title, i18n.language);
  return (
    <ContentLangProvider lang={contentLang}>
    <div className="ac-page ac-lesson">
      <Link to={backTo} className="ac-back">
        <ArrowLeft aria-hidden size={16} />
        {isMethod ? t("academyCourse.method.back") : ct("lesson.back")}
      </Link>
      <header className="ac-head">
        <div className="ac-head__text">
          <p className="ac-eyebrow ao-num">
            {isMethod && <Badge tone="warning">{t("academyCourse.method.title")}</Badge>} {lesson.code} · {ct("course.unit", { code: lesson.unit.code })} ·{" "}
            <span lang={contentLang}>{pick(lesson.unit.title, contentLang)}</span>
          </p>
          <h1 ref={heading} tabIndex={-1} className="ac-head__title" lang={contentLang}>
            {title}
          </h1>
          {subtitle && subtitle !== title && <p className="ac-head__subtitle">{subtitle}</p>}
        </div>
        <div className="ac-head__aside">
          <Steps step={step} />
        </div>
      </header>

      {step === "learn" && (
        <LearnStep
          lesson={lesson}
          exam={exam}
          doAvoid={doAvoid}
          motherTongue={motherTongue}
          translation={translation}
          onTranslation={keepTranslation}
          onStart={() => setStep("practice")}
        />
      )}

      {step === "practice" && exercise && (
        <section className="ac-lesson__practice" aria-label={t("ielts.lesson.step.practice")}>
          <div className="ac-lesson__bar">
            <p className="ac-question__count ao-num">{t("ielts.lesson.exerciseOf", { n: index + 1, total })}</p>
            <div className="ac-progress" role="progressbar" aria-label={t("ielts.lesson.progressLabel")} aria-valuemin={0} aria-valuemax={total} aria-valuenow={index + (answeredNow ? 1 : 0)}>
              <span style={{ transform: `scaleX(${total ? (index + (answeredNow ? 1 : 0)) / total : 0})` }} />
            </div>
            <Badge tone="offer">
              <Sparkles aria-hidden size={12} /> {t("ielts.lesson.xpNow", { xp })}
            </Badge>
          </div>
          <div className="ac-question__sheet">
            <ExerciseCard
              key={exercise.id}
              item={exercise}
              done={exercise.done}
              onCheck={(response) => check(exercise.id, response)}
              onRecording={(audio) => sendRecording(exercise.id, audio)}
              translated={translation?.exercises[exercise.id] ?? ""}
            />
          </div>
          {answeredNow && (
            <div className="ac-question__actions">
              <Button size="lg" onClick={goNext}>
                {index + 1 < total ? t("ielts.lesson.nextExercise") : t("ielts.lesson.finish")}
                <ArrowRight aria-hidden size={18} />
              </Button>
            </div>
          )}
        </section>
      )}

      {step === "end" && (
        <GlassCard as="section" className="ac-card ac-lesson-end" aria-labelledby="ac-lesson-end">
          <div className="ac-lesson-end__ring">
            <Ring ratio={score} size={168} stroke={14} tone={passed ? "success" : "warning"}>
              <span className="ac-lesson-end__value ao-num">{Math.round(score * 100)} %</span>
              <span className="ac-caption">{t("ielts.lesson.score")}</span>
            </Ring>
          </div>
          <div className="ac-lesson-end__text">
            <p className="ac-eyebrow">
              {passed ? <PartyPopper aria-hidden size={14} /> : <RotateCcw aria-hidden size={14} />} {t("ielts.lesson.endEyebrow")}
            </p>
            <h2 id="ac-lesson-end" className="ac-title-2">
              {passed ? t("ielts.lesson.passed") : t("ielts.lesson.notPassed")}
            </h2>
            <p className="ac-secondary">{passed ? t("ielts.lesson.passedText") : t("ielts.lesson.notPassedText")}</p>
            <ul className="ac-challenge-hero__facts">
              <li>
                <ListChecks aria-hidden size={15} /> {t("ielts.lesson.rightCount", { right, total: autoCount })}
              </li>
              <li className="ac-xp">
                <Sparkles aria-hidden size={15} /> {t("ielts.lesson.xpWon", { count: Math.max(xp, progress?.xpEarned ?? 0) })}
              </li>
            </ul>
            <div className="ac-card__actions ac-card__actions--start">
              <Button variant="ghost" icon={<RotateCcw aria-hidden size={16} />} onClick={() => void restart()} disabled={restarting}>
                {t("ielts.lesson.restart")}
              </Button>
              <Link className={buttonClass("secondary")} to={backTo}>
                {isMethod ? t("academyCourse.method.back") : ct("lesson.toPath")}
              </Link>
              {nextLesson && passed && (
                <Button onClick={() => navigate(`/academy/course/lesson/${nextLesson}`)}>
                  {t("ielts.lesson.nextLesson")}
                  <ArrowRight aria-hidden size={18} />
                </Button>
              )}
            </div>
          </div>
        </GlassCard>
      )}
      <p className="ac-footnote">{ct("estimate")}</p>
    </div>
    </ContentLangProvider>
  );
}
